import {
  Alert,
  Card,
  Descriptions,
  Empty,
  List,
  Space,
  Tag,
  Typography,
} from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import type { PreviewPlanFromWizardResponseDto } from '@evimo/client/models';
import { useTranslation } from 'react-i18next';
import { PMP_TAG_COLOR } from '~/app/components/maintenance/pmp-tag-colors';
import type { WizardData } from '~/app/components/maintenance/preventive/wizard/types';
import { unresolvedPreviewConflicts } from '~/app/components/maintenance/preventive/wizard/StepAssets/utils';
import { PRIORITY_COLOR } from '../../utils';

const { Text } = Typography;

export interface PlanWizardReviewStepProps {
  data: WizardData;
  preview: PreviewPlanFromWizardResponseDto | undefined;
  isPreviewing: boolean;
  previewError: string | null;
}

export function PlanWizardReviewStep({
  data,
  preview,
  isPreviewing,
  previewError,
}: PlanWizardReviewStepProps) {
  const { t } = useTranslation('maintenance');
  const unresolvedConflicts = preview
    ? unresolvedPreviewConflicts(preview.conflicts, data.replaceAssetIds)
    : [];
  const resolvedReplaceCount = preview
    ? preview.conflicts.length - unresolvedConflicts.length
    : 0;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {isPreviewing && (
        <Alert
          type="info"
          showIcon
          message={t('wizard.review.previewLoading')}
        />
      )}
      {previewError && <Alert type="error" showIcon message={previewError} />}
      {preview && preview.validationErrors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={t('wizard.review.validationErrorsTitle')}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {preview.validationErrors.map((err) => (
                <li key={`${err.field}-${err.message}`}>
                  <Text strong>{err.field}</Text>: {err.message}
                </li>
              ))}
            </ul>
          }
        />
      )}
      {preview && preview.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t('wizard.review.warningsTitle')}
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {preview.warnings.map((w) => (
                <li key={w.code}>{w.message}</li>
              ))}
            </ul>
          }
        />
      )}
      {unresolvedConflicts.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={t('wizard.review.conflictsTitle', {
            count: unresolvedConflicts.length,
          })}
        />
      )}
      {resolvedReplaceCount > 0 && unresolvedConflicts.length === 0 && (
        <Alert
          type="info"
          showIcon
          message={t('wizard.review.replacingCount', {
            count: resolvedReplaceCount,
          })}
        />
      )}
      {preview && (
        <Card title={t('wizard.review.previewSummary')}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label={t('wizard.review.summaryTaskCount')}>
              {preview.summary.taskCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('wizard.review.summaryAssetCount')}>
              {preview.summary.requestedAssetCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('wizard.review.summaryConflictCount')}>
              {preview.summary.conflictCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('wizard.review.summaryReplaceCount')}>
              {preview.summary.replaceCount}
            </Descriptions.Item>
            <Descriptions.Item label={t('wizard.review.summaryCreateCount')}>
              {preview.summary.createAssignmentCount}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Card title={t('wizard.review.planDetails')}>
        <Descriptions column={2} bordered size="small">
          <Descriptions.Item label={t('wizard.general.code')}>
            {data.code}
          </Descriptions.Item>
          <Descriptions.Item label={t('wizard.general.name')}>
            {data.name}
          </Descriptions.Item>
          <Descriptions.Item label={t('wizard.general.priority')}>
            <Tag color={PRIORITY_COLOR[data.priority] ?? 'default'}>
              {t(`priority.${data.priority}`)}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('wizard.general.schedulingHorizonDays')}>
            {t('wizard.general.schedulingHorizonDaysValue', {
              days: data.schedulingHorizonDays,
            })}
          </Descriptions.Item>
          <Descriptions.Item label={t('wizard.general.active')}>
            {data.active ? t('common.yes') : t('common.no')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card title={t('wizard.review.taskList', { count: data.tasks.length })}>
        {data.tasks.length === 0 ? (
          <Empty />
        ) : (
          <List
            size="small"
            dataSource={data.tasks}
            renderItem={(task, i) => (
              <List.Item>
                <Space>
                  <Tag>{i + 1}</Tag>
                  <Text strong>{task.name}</Text>
                  {task.triggerLabel ? (
                    <Tag color="purple">{task.triggerLabel}</Tag>
                  ) : (
                    <Tag>—</Tag>
                  )}
                  <Tag color={PMP_TAG_COLOR.workType}>{task.workTypeLabel}</Tag>
                  {task.mandatory && (
                    <Tag color="green">{t('tasks.mandatory')}</Tag>
                  )}
                </Space>
              </List.Item>
            )}
          />
        )}
      </Card>

      <Card title={t('wizard.review.assetAssignments')}>
        {data.assetIds.length === 0 ? (
          <Text type="secondary">{t('wizard.review.noAssets')}</Text>
        ) : (
          <Space direction="vertical">
            <Text>
              {t('wizard.review.assetsCount', {
                count: data.assetIds.length,
                date: data.startDate || '—',
              })}
            </Text>
            {data.replaceAssetIds.length > 0 && (
              <Text type="warning">
                <WarningOutlined />{' '}
                {t('wizard.review.replacingCount', {
                  count: data.replaceAssetIds.length,
                })}
              </Text>
            )}
          </Space>
        )}
      </Card>
    </Space>
  );
}
